import {
	Grid,
	Card,
	CardHeader,
	CardContent,
	List,
	ListItem,
	ListItemIcon,
	Checkbox,
	ListItemText,
	Button,
	CircularProgress,
	Typography
} from '@mui/material';
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { listDNCLists } from "../../../graphql/queries";
import { fetchAllPages } from '../../../tools/graphqlPaging';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle } from '../../../theme';

export default memo(function DNCLists(props) {
	const { formikProps, checkIndex, errors, touched } = props;

	const client = generateClient();

	const lists = formikProps.values?.checks?.[checkIndex]?.dncListSuppressions?.dncs ?? [];

	const formikSet = (value) => {
		formikProps.setFieldValue(
			`checks.${checkIndex}.dncListSuppressions.dncs`,
			value
		);
	};

	const [loading, setLoading] = useState(false);
	const [availableLists, setAvailableLists] = useState([]);
	const rawListsMap = useRef({});

	const getData = useCallback(async () => {
		setLoading(true);
		const dynamoLists = await fetchAllPages({
			client,
			query: listDNCLists,
			resultKey: 'listDNCLists',
		});

		const updatedLists = dynamoLists.map(x =>
			Object.assign(x, { selected: false })
		);

		setAvailableLists(updatedLists.filter(x => !lists.includes(x.id)));

		rawListsMap.current = updatedLists.reduce(
			(acc, cur) => Object.assign(acc, { [cur.id]: cur }),
			{}
		);

		setLoading(false);
	}, [lists]);

	useEffect(() => {
		getData();
	}, []);

	const handleAdd = useCallback(() => {
		setAvailableLists(av => {
			const toAdd = [];
			const remaining = [];

			for (const list of av) {
				if (list.selected) {
					toAdd.push(list.id);
					list.selected = false;
				} else remaining.push(list);
			}

			formikSet([...lists, ...toAdd]);
			return remaining;
		});
	}, [lists]);

	const handleRemove = useCallback(() => {
		setAvailableLists(av => {
			const removed = [];
			const keep = [];

			for (const listId of lists) {
				if (rawListsMap.current[listId].selected) {
					removed.push(rawListsMap.current[listId]);
					rawListsMap.current[listId].selected = false;
				} else {
					keep.push(listId);
				}
			}

			formikSet([...keep]);
			return [...av, ...removed];
		});
	}, [lists]);

	const handleAvailableToggle = useCallback(index => {
		setAvailableLists(items => {
			items[index].selected = !items[index].selected;
			return [...items];
		});
	}, []);

	const handleSelectedToggle = useCallback(id => {
		rawListsMap.current[id].selected = !rawListsMap.current[id].selected;
		// force rerender
		setAvailableLists(av => [...av]);
	}, []);

	const handleAddAll = useCallback(() => {
		setAvailableLists(av => {
			for (const list of av) list.selected = false;
			formikSet([...lists, ...av.map(x => x.id)]);
			return [];
		});
	}, [lists]);

	const handleRemoveAll = useCallback(() => {
		setAvailableLists(av => {
			formikSet([]);

			return [
				...av,
				...lists.map(id => {
					rawListsMap.current[id].selected = false;
					return rawListsMap.current[id];
				})
			];
		});
	}, [lists]);

	const errorText =
		errors?.checks?.[checkIndex]?.dncListSuppressions ??
		formikProps.errors?.checks?.[checkIndex]?.dncListSuppressions;

	const touchedBlock =
		touched?.checks?.[checkIndex]?.dncListSuppressions ??
		formikProps.touched?.checks?.[checkIndex]?.dncListSuppressions;

	return (
		<Grid container spacing={2} direction='column'>
			{errorText && touchedBlock && (
				<Grid item>
					<Typography variant='body1' color='error'>
						{errorText}
					</Typography>
				</Grid>
			)}

			{loading && (
				<Grid item>
					<CircularProgress variant='indeterminate' color='primary' />
				</Grid>
			)}

			{!loading && (
				<Grid item>
					<Grid container spacing={2} alignItems='center'>
						{/* AVAILABLE LISTS */}
						<Grid item>
							<Card variant='outlined' sx={cardStyle}>
								<CardHeader title='Available Lists' />
								<CardContent>
									<List dense>
										{availableLists.map((list, index) => (
											<FastListItem
												name={list.name}
												id={list.id}
												selected={list.selected}
												key={index}
												onClick={() => handleAvailableToggle(index)}
											/>
										))}
									</List>
								</CardContent>
							</Card>
						</Grid>

						{/* BUTTONS */}
						<Grid item>
							<Grid container spacing={1} direction='column' justifyContent='center'>
								<Grid item>
									<Button sx={actionTwoButtonStyle} onClick={handleAddAll}>≫</Button>
								</Grid>
								<Grid item>
									<Button sx={actionOneButtonStyle} onClick={handleAdd}>&gt;</Button>
								</Grid>
								<Grid item>
									<Button sx={actionOneButtonStyle} onClick={handleRemove}>&lt;</Button>
								</Grid>
								<Grid item>
									<Button sx={actionTwoButtonStyle} onClick={handleRemoveAll}>≪</Button>
								</Grid>
							</Grid>
						</Grid>

						{/* SELECTED LISTS */}
						<Grid item>
							<Card variant='outlined' sx={cardStyle}>
								<CardHeader title='Selected Lists' />
								<CardContent>
									<List dense>
										{Object.keys(rawListsMap.current).length > 0 &&
											lists.map(listId => (
												<FastListItem
													key={listId}
													name={rawListsMap.current[listId]?.name}
													id={listId}
													selected={rawListsMap.current[listId]?.selected}
													onClick={() => handleSelectedToggle(listId)}
												/>
											))}
									</List>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				</Grid>
			)}
		</Grid>
	);
}, (prev, next) => {
	return (
		prev.formikProps.values?.checks?.[prev.checkIndex]?.dncListSuppressions?.dncs ===
		next.formikProps.values?.checks?.[next.checkIndex]?.dncListSuppressions?.dncs &&
		prev.errors === next.errors &&
		prev.touched === next.touched
	);
});

const FastListItem = memo(function FastListItem(props) {
	return (
		<ListItem onClick={props.onClick}>
			<ListItemIcon>
				<Checkbox
					color='primary'
					checked={props.selected ?? false}
				/>
			</ListItemIcon>
			<ListItemText primary={props.name} />
		</ListItem>
	);
}, (prev, next) => prev.id === next.id && prev.selected === next.selected);
